/**
 * Button-level help catalog used by Help Mode.
 *
 * Two layers:
 *  1. EXACT: keyed by `data-testid` (or a `prefix:` for ones that end in an
 *     id like `btn-delete-123`). Highest priority — lets us write detailed,
 *     contextual help per button.
 *  2. PATTERNS: regex/keyword fallback over testid + label + textContent.
 *     Catches the long tail (any "save" button anywhere, any "delete" button,
 *     any stat card, any tab, etc) so even un-cataloged controls get a useful
 *     bilingual explanation instead of just echoing their text.
 *
 * Each entry is { ar, en } with a 1-3 sentence explanation of WHAT the button
 * does and (when useful) WHEN to use it / what to expect.
 */

export interface ButtonHelp {
  ar: string;
  en: string;
}

/**
 * Exact-match catalog. Key = full data-testid, OR a string ending with `*`
 * to match by prefix (e.g. `stat-card-*` matches `stat-card-orders`).
 */
const EXACT: Record<string, ButtonHelp> = {
  // ─── Notifications page ────────────────────────────────────────────────
  "btn-refresh": {
    ar: "يُحدِّث القائمة الحالية ويجلب أحدث البيانات من السيرفر بدون إعادة تحميل الصفحة كاملة.",
    en: "Refreshes the current list and pulls the latest data from the server without a full page reload.",
  },
  "btn-mark-all-read-page": {
    ar: "يُعلِّم كل الإشعارات في القائمة الحالية كمقروءة دفعة واحدة، بدون حذفها. مفيد لتنظيف عدّاد الإشعارات غير المقروءة.",
    en: "Marks every notification on the current list as read in one go (does not delete them). Useful for clearing the unread badge.",
  },
  "btn-cleanup": {
    ar: "يحذف نهائياً الإشعارات المقروءة القديمة (أقدم من 30 يوماً) لتنظيف الأرشيف. هذا الإجراء لا يمكن التراجع عنه.",
    en: "Permanently deletes old read notifications (older than 30 days) to clean up the archive. This cannot be undone.",
  },
  "btn-cancel-delete-notif": {
    ar: "يُلغي عملية الحذف ويُغلق هذا الحوار دون أي تغيير.",
    en: "Cancels the delete and closes this dialog without making any change.",
  },
  "btn-confirm-delete-notif": {
    ar: "يؤكِّد حذف الإشعار نهائياً. لن يمكن استعادته بعد هذا الإجراء.",
    en: "Confirms permanent deletion of the notification. It cannot be restored afterwards.",
  },
  "btn-confirm-cleanup": {
    ar: "يؤكِّد حذف كل الإشعارات المقروءة القديمة دفعة واحدة. الإجراء نهائي.",
    en: "Confirms bulk deletion of all old read notifications. This is final.",
  },

  // ─── Stat cards (notifications page filter the list by category) ───────
  "stat-card-orders": {
    ar: "كرت إحصائي يعرض عدد إشعارات الطلبات. اضغطه لتصفية القائمة وعرض إشعارات الطلبات فقط.",
    en: "Stat card showing the count of order notifications. Tap to filter the list to orders only.",
  },
  "stat-card-customers": {
    ar: "كرت إحصائي يعرض عدد إشعارات العملاء (تسجيلات جديدة، طلبات تواصل). اضغطه لتصفية القائمة على إشعارات العملاء فقط.",
    en: "Stat card showing the count of customer notifications (registrations, contact requests). Tap to filter the list to customers only.",
  },
  "stat-card-logistics": {
    ar: "كرت إحصائي يعرض إشعارات اللوجستيات (طلبات صرف، تحديثات شحن، طلبات شراء). اضغطه لتصفية القائمة على إشعارات اللوجستيات فقط.",
    en: "Stat card showing logistics notifications (stock requests, shipping updates, purchase orders). Tap to filter the list to logistics only.",
  },
  "stat-card-messages": {
    ar: "كرت إحصائي يعرض رسائل التواصل الواردة من العملاء. اضغطه لتصفية القائمة على الرسائل فقط.",
    en: "Stat card showing incoming customer messages. Tap to filter the list to messages only.",
  },
  "stat-card-reviews": {
    ar: "كرت إحصائي يعرض إشعارات المراجعات الجديدة من العملاء. اضغطه لتصفية القائمة على المراجعات فقط.",
    en: "Stat card showing new customer reviews. Tap to filter the list to reviews only.",
  },
  "stat-card-marketing": {
    ar: "كرت إحصائي يعرض إشعارات التسويق (حملات، كوبونات، تحويلات الإحالة). اضغطه لتصفية القائمة عليها.",
    en: "Stat card showing marketing notifications (campaigns, coupons, referral conversions). Tap to filter the list to those.",
  },

  // ─── Tabs ──────────────────────────────────────────────────────────────
  // ── Push notifications page ────────────────────────────────────────────
  "tab-push-notifications": {
    ar: "تبويب \"الإشعارات\": يعرض كل إشعارات Push التي أنشأتِها (مسوّدات، مجدولة، مُرسلة) مع حالة كل واحد وعدد المشتركين الذين وصلهم.",
    en: "'Notifications' tab: lists every Push notification you've created (drafts, scheduled, sent) with status and how many subscribers received each one.",
  },
  "tab-push-subscribers": {
    ar: "تبويب \"المشتركون\": يعرض قائمة الأجهزة التي اشتركت لاستقبال إشعارات Push من متجرك، مع تقسيم حسب نوع الجهاز (موبايل/ديسكتوب) والمتصفح، وتاريخ الاشتراك. لا يُرسِل شيئاً، فقط يعرض المعلومات.",
    en: "'Subscribers' tab: lists every device that opted-in to receive your Push notifications, broken down by device type (mobile/desktop), browser, and signup date. View-only — sending happens from the Notifications tab.",
  },
  "btn-subscribe-this-device": {
    ar: "يطلب من المتصفح إذن الإشعارات ثم يُسجِّل هذا الجهاز كمشترك. مفيد لاختبار الإشعارات على نفسك قبل إرسالها للعملاء. يعمل فقط على المتصفحات الحديثة (Chrome/Edge/Firefox، وSafari iOS بعد إضافة الموقع للشاشة الرئيسية).",
    en: "Asks the browser for notification permission and registers THIS device as a subscriber. Useful for testing notifications on yourself before sending to customers. Works only on modern browsers (Chrome/Edge/Firefox, and Safari iOS after Add-to-Home-Screen).",
  },
  "btn-new-push-notification": {
    ar: "يفتح نافذة إنشاء إشعار جديد: العنوان والمحتوى بالعربية والإنجليزية ورابط الوجهة. الإشعار يُحفظ كمسوّدة ولا يُرسَل تلقائياً — تحتاجين لاحقاً للضغط على \"إرسال\" بجانبه.",
    en: "Opens the dialog to create a new push notification: title + body (AR & EN) and a destination URL. The notification is saved as a draft — it is NOT sent automatically; you must click 'Send' next to it later.",
  },
  "btn-create-push-notification": {
    ar: "يحفظ الإشعار الذي كتبته كـ مسوّدة في قاعدة البيانات. لا يُرسَل للمشتركين بعد — افتح القائمة واضغط \"إرسال\" بجانبه عندما تكون جاهز.",
    en: "Saves the notification you just composed as a DRAFT in the database. It is not sent to subscribers yet — open the list and click 'Send' next to it when you're ready.",
  },
  "btn-send-push-*": {
    ar: "يُرسِل هذا الإشعار فوراً لجميع المشتركين الفعّالين عبر بروتوكول Web Push. الإرسال نهائي ولا يمكن استرجاعه. يصل الإشعار حتى لو كان متصفح العميل مغلقاً.",
    en: "Sends this notification IMMEDIATELY to every active subscriber via Web Push. The send is final and cannot be recalled. It arrives even if the customer's browser is closed.",
  },
  "btn-delete-push-*": {
    ar: "يحذف هذا الإشعار من القائمة نهائياً. إن كان قد أُرسِل من قبل، الحذف هنا لا يمنع وصوله للأجهزة — فقط يحذف السجل من لوحة التحكم.",
    en: "Permanently deletes this notification from the list. If it was already sent, deleting here does NOT recall it from devices — it only removes the record from the admin panel.",
  },
  "tab-all": {
    ar: "يعرض كل الإشعارات (مقروءة وغير مقروءة) ضمن الفلاتر الحالية.",
    en: "Shows all notifications (read and unread) within the current filters.",
  },
  "tab-unread": {
    ar: "يعرض فقط الإشعارات غير المقروءة. الرقم بجانب التبويب يدلّ على إجمالي غير المقروء.",
    en: "Shows only unread notifications. The number on the tab is the total unread count.",
  },
  "tab-archived": {
    ar: "يعرض الإشعارات المؤرشفة (التي اخترت إخفاءها من القائمة الرئيسية لكن لم تحذفها).",
    en: "Shows archived notifications (hidden from the main list but not deleted).",
  },

  // ─── Filter selects ────────────────────────────────────────────────────
  "select-category": {
    ar: "يفلتر الإشعارات حسب الفئة (طلبات، عملاء، لوجستيات، …). اختر فئة لرؤية إشعاراتها فقط.",
    en: "Filters notifications by category (orders, customers, logistics, …). Pick one to see only that category.",
  },
  "select-severity": {
    ar: "يفلتر حسب درجة الأهمية (حرجة، مهمة، عادية، إعلامية). يساعد على التركيز على الأهم أولاً.",
    en: "Filters by severity (critical, important, normal, info). Helps focus on the most urgent first.",
  },

  // ─── Per-row notification actions (prefix match) ──────────────────────
  "btn-open-*": {
    ar: "يفتح الإشعار ويأخذك مباشرة إلى الصفحة المتعلقة به (الطلب، العميل، الرسالة …) ويُعلِّمه كمقروء.",
    en: "Opens the notification and jumps straight to its related page (order, customer, message …), marking it as read.",
  },
  "btn-read-*": {
    ar: "يُعلِّم هذا الإشعار كمقروء بدون فتح الصفحة المتعلقة به.",
    en: "Marks this single notification as read without opening its related page.",
  },
  "btn-unread-*": {
    ar: "يُعيد هذا الإشعار إلى حالة غير مقروء كي تتذكّر متابعته لاحقاً.",
    en: "Marks this notification as unread again so you remember to follow up.",
  },
  "btn-archive-*": {
    ar: "يُؤرشف الإشعار: يخفيه من القائمة الرئيسية لكن لا يحذفه. يمكنك رؤيته في تبويب «المؤرشف».",
    en: "Archives the notification: hides it from the main list but does not delete it. You can still find it in the «Archived» tab.",
  },
  "btn-delete-*": {
    ar: "يحذف هذا العنصر نهائياً. لا يمكن التراجع. سيُطلب منك التأكيد قبل الحذف الفعلي.",
    en: "Permanently deletes this item. Cannot be undone. You'll be asked to confirm before the actual delete.",
  },
  "row-notification-*": {
    ar: "صف إشعار واحد. يعرض الموضوع والتفاصيل ووقت الوصول، وأزرار الإجراءات في نهايته (فتح، أرشفة، حذف).",
    en: "A single notification row. Shows the subject, details, time received, and action buttons at the end (open, archive, delete).",
  },
  "notifications-list": {
    ar: "قائمة الإشعارات الكاملة المطابقة للفلاتر الحالية. كل سطر هو إشعار يمكنك التفاعل معه.",
    en: "The full notifications list matching your current filters. Each row is a notification you can interact with.",
  },

  // ─── Admin sidebar GROUP toggles (collapsible section headers) ─────────
  "sidebar-group-dashboard": {
    ar: "مجموعة «لوحة التحكم»: تجمع الصفحة الرئيسية ومركز الإشعارات. اضغط لطيّ المجموعة أو فتحها — لا يفتح صفحة بحد ذاته.",
    en: "“Dashboard” group: contains the home page and the notifications center. Tap to collapse or expand — it does not open a page itself.",
  },
  "sidebar-group-storefront": {
    ar: "مجموعة «واجهة المتجر»: كل ما يتعلّق بالمنتجات وعروضها — المنتجات، المزادات، الباقات، العروض الخاطفة، التغليف والمنتجات المصاحبة. اضغط لفتح/طي المجموعة.",
    en: "“Storefront” group: everything about products and offers — products, auctions, bundles, flash sales, packaging, companions. Tap to expand/collapse.",
  },
  "sidebar-group-sales": {
    ar: "مجموعة «المبيعات»: إدارة الطلبات، نقاط البيع (POS)، والمرتجعات. اضغط لعرض روابط هذه المجموعة.",
    en: "“Sales” group: manage orders, Point of Sale (POS), and returns. Tap to expand its links.",
  },
  "sidebar-group-logistics": {
    ar: "مجموعة «اللوجستك»: لوحة اللوجستيات وإدارة الشحنات. اضغط لفتح/طي المجموعة.",
    en: "“Logistics” group: logistics dashboard and shipments. Tap to expand/collapse.",
  },
  "sidebar-group-marketing": {
    ar: "مجموعة «التسويق»: قنوات التواصل والتفاعل مع العميل — الكوبونات، برامج الولاء، الشات بوت، البريد، الاشتراكات، التقييمات.",
    en: "“Marketing” group: customer engagement channels — coupons, loyalty, on-site chatbot, email, subscriptions, reviews.",
  },
  "sidebar-group-marketing-tools": {
    ar: "مجموعة «أدوات التسويق»: SEO، إشعارات Push، جدولة المحتوى، التقييمات الآلية، قوالب الرسائل، الحملات الإعلانية المتقدمة.",
    en: "“Marketing Tools” group: SEO, Push notifications, content scheduling, auto-reviews, message templates, advanced ad campaigns.",
  },
  "sidebar-group-customers": {
    ar: "مجموعة «العملاء»: قائمة العملاء، المجموعات والشرائح، حقول التسجيل المخصصة، حسابات شركاء B2B، قوائم الأمنيات، السلال المهجورة.",
    en: "“Customers” group: customer list, groups & segments, custom registration fields, B2B partner accounts, wishlists, abandoned carts.",
  },
  "sidebar-group-reports": {
    ar: "مجموعة «التقارير»: لوحات تحليلات شاملة — أداء المبيعات، Business Intelligence، تقارير CAPI، مقارنات وتدقيق الإيرادات.",
    en: "“Reports” group: comprehensive analytics — sales performance, Business Intelligence, CAPI reports, comparisons and revenue audits.",
  },
  "sidebar-group-content": {
    ar: "مجموعة «المحتوى»: الصفحات الثابتة، الفلاتر الجانبية، أدلة المقاسات، الماركات، الأقسام، خصائص المنتجات وخياراتها.",
    en: "“Content” group: static pages, sidebar filters, size guides, brands, categories, product attributes and options.",
  },
  "sidebar-group-extensions": {
    ar: "مجموعة «الإضافات»: الإضافات المثبَّتة، التخطيطات، الموديولات، طرق الدفع والشحن (الجديدة)، مدير الملفات، متجر الإضافات.",
    en: "“Extensions” group: installed extensions, layouts, modules, new payment & shipping methods, file manager, marketplace.",
  },
  "sidebar-group-accounting": {
    ar: "مجموعة «المحاسبة»: النظام المحاسبي، أوامر الشراء، الموردين، المخزون، المستودعات، شركات B2B، الضرائب وZATCA، تاريخ الأسعار.",
    en: "“Accounting” group: accounting hub, purchase orders, suppliers, inventory, warehouses, B2B companies, taxes & ZATCA, price history.",
  },
  "sidebar-group-quality": {
    ar: "مجموعة «الجودة»: تقارير ROI الحملات، تحليلات قوائم الأمنيات، تتبّع التحويلات، ومراقبة جودة بيانات المتجر.",
    en: "“Quality” group: campaign ROI, wishlist analytics, conversion tracking, and overall store-data quality monitoring.",
  },
  "sidebar-group-blog": {
    ar: "مجموعة «المدونة»: إعدادات المدونة، الأقسام، المقالات، التعليقات، واستوديو الذكاء الاصطناعي لتوليد المحتوى.",
    en: "“Blog” group: blog settings, categories, posts, comments, and the AI Studio for content generation.",
  },
  "sidebar-group-store-settings": {
    ar: "مجموعة «إعدادات المتجر»: إعدادات عامة، إعدادات الإشعارات، الدومين المخصص، مجموعات الصلاحيات، اللغات.",
    en: "“Store Settings” group: general settings, notification settings, custom domain, user groups (ACL), languages.",
  },
  "sidebar-group-content-settings": {
    ar: "مجموعة «إعدادات المحتوى»: إعدادات المرتجعات والطلبات، وحدات الوزن والطول، الدول والمناطق، حالات الطلبات، مناطق الشحن.",
    en: "“Content Settings” group: returns & order config, weight/length units, countries & zones, order statuses, shipping zones.",
  },
  "sidebar-group-developer-settings": {
    ar: "مجموعة «إعدادات التطوير»: متعدد المتاجر، Webhooks، الفهارس، الأمان (2FA)، التطبيقات، الاستيراد/التصدير، إدارة الكاش، أداء النظام، النسخ الاحتياطي، CSS مخصص.",
    en: "“Developer Settings” group: multi-store, webhooks, indexes, 2FA security, apps, import/export, cache, system performance, backups, custom CSS.",
  },

  // ─── Admin sidebar LINKS (page navigations) ────────────────────────────
  "sidebar-link-home": {
    ar: "ينقلك إلى لوحة التحكم الرئيسية: ملخّص المبيعات اليوم، أحدث الطلبات، حالة المتجر، ومؤشّرات الأداء.",
    en: "Goes to the main dashboard: today's sales summary, latest orders, store status, and KPIs.",
  },
  "sidebar-link-notifications": {
    ar: "يفتح مركز الإشعارات: كل أحداث المتجر (طلبات، عملاء، مخزون، تسويق، رسائل) مع فلاتر حسب الفئة وغير المقروء.",
    en: "Opens the notifications center: every store event (orders, customers, stock, marketing, messages) with category and unread filters.",
  },
  "sidebar-link-products": {
    ar: "صفحة المنتجات الرئيسية: عرض، بحث، إضافة، تعديل، حذف، وتصفية كل منتجات متجرك. هنا تُضبط الأسعار والمخزون والصور والمواصفات.",
    en: "Main products page: view, search, add, edit, delete, and filter every product. Set prices, stock, images, and specs from here.",
  },
  "sidebar-link-products-bulk": {
    ar: "إدارة المنتجات بالجملة: تعديل الأسعار، المخزون، الفئات، أو الحالة لعدد كبير من المنتجات دفعة واحدة.",
    en: "Bulk product management: change prices, stock, categories, or status for many products at once.",
  },
  "sidebar-link-new-arrivals": {
    ar: "إدارة قسم «وصل حديثاً»: تحديد المنتجات التي تظهر كـ«جديد» في الواجهة وفترة بقائها.",
    en: "Manage the “New Arrivals” section: choose which products show as “New” on the storefront and for how long.",
  },
  "sidebar-link-grouped-products": {
    ar: "المنتجات المُجمَّعة: تجميع منتجات مرتبطة (مثلاً ألوان متعدّدة) ضمن صفحة واحدة في المتجر.",
    en: "Grouped products: tie related items (e.g. color variants) under a single storefront page.",
  },
  "sidebar-link-related-rules": {
    ar: "قواعد المنتجات المرتبطة: ضبط منطق العرض التلقائي لـ«قد يعجبك أيضاً» بناءً على القسم أو الماركة أو وسم.",
    en: "Related-products rules: configure how “You may also like” is auto-populated by category, brand, or tag.",
  },
  "sidebar-link-multi-offers": {
    ar: "العروض المتعددة: تخفيضات بصِيغ متنوعة (اشتر X خذ Y، خصم على ثاني قطعة، ...).",
    en: "Multi-offers: discount campaigns in various formats (buy X get Y, % off the second item, …).",
  },
  "sidebar-link-auctions": {
    ar: "المزادات: إنشاء مزاد على منتج، تحديد الحد الأدنى والمدة، مراقبة المزايدات وإغلاق المزاد.",
    en: "Auctions: launch an auction on a product, set min bid and duration, monitor bids and close the auction.",
  },
  "sidebar-link-bundle-offers": {
    ar: "عروض الباقات: قواعد خصم تلقائي عند شراء مجموعة منتجات معاً.",
    en: "Bundle offers: automatic discount rules when a customer buys a defined set of products together.",
  },
  "sidebar-link-flash-sales": {
    ar: "العروض الخاطفة: تخفيضات محدودة بوقت قصير مع عدّاد تنازلي يظهر في صفحة المنتج.",
    en: "Flash sales: time-limited discounts with a countdown shown on the product page.",
  },
  "sidebar-link-bundles": {
    ar: "الباقات: إنشاء حزم تجمع عدة منتجات بسعر إجمالي ثابت كأنّها منتج واحد.",
    en: "Bundles: build packs that combine multiple products at a fixed package price as a single item.",
  },
  "sidebar-link-companion-lists": {
    ar: "المنتجات المصاحبة والتغليف: ضبط منتجات تُعرض كإضافات (إجبارية ضمن السعر أو اختيارية بسعر إضافي) في صفحة منتج معيّن.",
    en: "Companion products & wrapping: configure add-ons (mandatory included or optional paid) shown on a product page.",
  },
  "sidebar-link-bulk-actions": {
    ar: "العمليات الجماعية: تنفيذ إجراءات على مجموعة منتجات/طلبات/عملاء محدّدة (تغيير حالة، حذف، تصدير ...).",
    en: "Bulk actions: run operations on a selected set of products/orders/customers (status change, delete, export …).",
  },
  "sidebar-link-orders": {
    ar: "الطلبات: قائمة كل الطلبات مع الفلاتر (الحالة، التاريخ، العميل، طريقة الدفع/الشحن). من هنا تفتحين كل طلب لتجهيزه.",
    en: "Orders: full list of orders with filters (status, date, customer, payment/shipping). Open any order to fulfill it.",
  },
  "sidebar-link-pos": {
    ar: "نقاط البيع (POS): واجهة كاشير لإصدار طلبات داخل المحل أو المعرض، مع طباعة الفاتورة وقبول الدفع كاش/شبكة.",
    en: "Point of Sale (POS): a cashier interface for in-store sales — issue invoices and accept cash/card payments.",
  },
  "sidebar-link-returns": {
    ar: "المرتجعات: إدارة طلبات الإرجاع من العملاء، الموافقة، إصدار رصيد أو استرداد، وتحديث المخزون.",
    en: "Returns: handle customer return requests, approve them, issue credit or refund, and update stock.",
  },
  "sidebar-link-logistics": {
    ar: "لوحة اللوجستيات: نظرة موحَّدة على شحنات اليوم، حالات التوصيل، طلبات صرف المخزون والمواعيد المتأخّرة.",
    en: "Logistics dashboard: unified view of today's shipments, delivery statuses, stock pulls, and overdue items.",
  },
  "sidebar-link-shipments": {
    ar: "الشحنات: إنشاء بوليصة شحن لكل طلب، طباعة ملصقات، تتبّع الحالة لدى شركة الشحن.",
    en: "Shipments: create a waybill per order, print shipping labels, and track status with the carrier.",
  },
  "sidebar-link-marketing-tracking": {
    ar: "تتبّع التسويق: ربط بكسلات (Meta/TikTok/Snap) ومتابعة UTM لقياس مصدر الزوار وتحويلهم.",
    en: "Marketing tracking: connect pixels (Meta/TikTok/Snap) and capture UTM data to measure traffic source and conversions.",
  },
  "sidebar-link-coupons": {
    ar: "الكوبونات: إنشاء أكواد خصم بشروط (نسبة/مبلغ، حد أدنى للسلة، فترة صلاحية، استعمال لمرة واحدة).",
    en: "Coupons: create discount codes with rules (percentage/amount, min cart, expiry, one-time use).",
  },
  "sidebar-link-loyalty-hub": {
    ar: "مركز الولاء الموحَّد: نقاط ومستويات للعملاء، قواعد كسب النقاط واستبدالها بخصومات.",
    en: "Unified Loyalty Hub: customer points and tiers, rules for earning points and redeeming them for discounts.",
  },
  "sidebar-link-site-chat": {
    ar: "الشات بوت داخل الموقع: تكوين روبوت محادثة يردّ على الزوار في صفحات المتجر مع إمكانية تدريبه على بياناتك.",
    en: "On-site chatbot: configure an in-store chat bot that answers visitors and can be trained on your data.",
  },
  "sidebar-link-email-campaigns": {
    ar: "إرسال بريد: حملات بريدية للعملاء (ترويجي، نشرة، إعلان عن منتج جديد) مع قوالب جاهزة وتقارير فتح.",
    en: "Email campaigns: send promotional/newsletter/launch emails to customers with templates and open-rate reports.",
  },
  "sidebar-link-subscriptions": {
    ar: "الاشتراكات: إدارة طلبات الاشتراك المتكرّر (شحن دوري لمنتج كل شهر مثلاً) والفوترة التلقائية.",
    en: "Subscriptions: manage recurring subscription orders (e.g. monthly delivery) and auto-billing.",
  },
  "sidebar-link-reviews": {
    ar: "تقييمات العملاء: مراجعة وقبول/رفض تقييمات المنتجات قبل ظهورها في المتجر.",
    en: "Customer reviews: moderate product reviews — approve or reject before they appear on the storefront.",
  },
  "sidebar-link-seo": {
    ar: "تحسين محركات البحث (SEO): إدارة عناوين الميتا، Sitemap، Robots.txt، إعادة التوجيه، البيانات المنظَّمة، وأدوات Analytics/Pixel.",
    en: "SEO: manage meta titles, sitemap, robots.txt, redirects, structured data, and Analytics/Pixel integrations.",
  },
  "sidebar-link-push-notifications": {
    ar: "إشعارات Push: إرسال إشعارات للأجهزة المشتركة (مثل تطبيقات الهاتف) — للترويج لعروض أو تنبيهات سريعة.",
    en: "Push notifications: send notifications to subscribed devices for promos or urgent alerts.",
  },
  "sidebar-link-content-staging": {
    ar: "جدولة المحتوى: نشر تغييرات (لافتات، أسعار، صفحات) في وقت محدّد بدون تدخّل يدوي.",
    en: "Content staging: schedule changes (banners, prices, pages) to publish at a specific time automatically.",
  },
  "sidebar-link-auto-reviews": {
    ar: "طلب التقييم التلقائي: إرسال رسالة (إيميل/واتساب) للعميل بعد فترة من استلام الطلب لطلب تقييم.",
    en: "Auto review requests: automatically email/WhatsApp customers after delivery to request a review.",
  },
  "sidebar-link-email-templates": {
    ar: "قوالب الرسائل: تخصيص نصوص ومظهر الإيميلات والـSMS التي يرسلها المتجر تلقائياً (تأكيد طلب، شحن، استرجاع كلمة مرور...).",
    en: "Message templates: customize the wording and styling of automatic emails/SMS (order confirm, shipping, password reset…).",
  },
  "sidebar-link-customers": {
    ar: "العملاء: قائمة كل عملاء المتجر مع تفاصيل كل عميل (طلباته، عناوينه، رصيده، نقاط الولاء).",
    en: "Customers: full customer list with each customer's profile (orders, addresses, balance, loyalty points).",
  },
  "sidebar-link-customer-groups": {
    ar: "مجموعات العملاء: تصنيف العملاء لمجموعات (VIP، جملة، عادي) لتطبيق أسعار/خصومات مختلفة عليهم.",
    en: "Customer groups: classify customers (VIP, wholesale, regular) to apply different prices/discounts.",
  },
  "sidebar-link-customer-segments": {
    ar: "شرائح العملاء: تجميع تلقائي حسب السلوك (أعلى مشتري، نشط، خامل، …) لاستهداف حملات تسويقية موجَّهة.",
    en: "Customer segments: automatic groupings by behavior (top spender, active, dormant…) for targeted marketing.",
  },
  "sidebar-link-customer-fields": {
    ar: "حقول التسجيل المخصصة: إضافة حقول إضافية في نموذج تسجيل العميل (شركة، رقم ضريبي، تفضيلات).",
    en: "Custom registration fields: add extra fields to the customer signup form (company, tax id, preferences).",
  },
  "sidebar-link-affiliates": {
    ar: "برنامج التسويق بالعمولة: إدارة المسوِّقين، روابطهم، عمولاتهم، وتقارير أدائهم.",
    en: "Affiliate program: manage affiliates, their referral links, commissions, and performance reports.",
  },
  "sidebar-link-wishlist": {
    ar: "قوائم الأمنيات: عرض المنتجات التي أضافها العملاء لقوائم رغباتهم — مفيد لمعرفة الأكثر طلباً.",
    en: "Wishlists: see products customers added to their wishlists — useful to know what's most desired.",
  },
  "sidebar-link-abandoned-carts": {
    ar: "السلال المهجورة: العملاء الذين تركوا منتجات في السلة دون إكمال الشراء — مع أدوات لاسترجاعهم بإيميل/واتساب.",
    en: "Abandoned carts: customers who left items in their cart — with tools to recover them via email/WhatsApp.",
  },
  "sidebar-link-analytics": {
    ar: "التحليلات: لوحات ورسوم بيانية لأداء المتجر (مبيعات، زيارات، تحويل، أفضل المنتجات).",
    en: "Analytics: dashboards and charts of store performance (sales, visits, conversion, top products).",
  },
  "sidebar-link-bi": {
    ar: "Business Intelligence: تقارير متقدمة قابلة للتخصيص بفلاتر متعدّدة (حسب الفترة، القسم، العميل، الحملة...).",
    en: "Business Intelligence: advanced customizable reports with multi-dimensional filters (period, category, customer, campaign…).",
  },
  "sidebar-link-comparison-report": {
    ar: "تقارير المقارنة: مقارنة الأداء بين فترات (هذا الشهر مقابل الماضي) أو بين منتجات/أقسام.",
    en: "Comparison reports: compare performance between periods (this month vs last) or between products/categories.",
  },
  "sidebar-link-revenue-audit": {
    ar: "تدقيق الإيرادات: فحص عمليات الدفع والطلبات للتأكّد من توافق الأرقام مع المحاسبة.",
    en: "Revenue audit: cross-check payments and orders to ensure numbers match accounting.",
  },
  "sidebar-link-pages": {
    ar: "الصفحات الثابتة: إنشاء وتعديل صفحات (من نحن، الشروط، سياسة الإرجاع، الأسئلة الشائعة).",
    en: "Static pages: create and edit pages (About, Terms, Refund Policy, FAQ).",
  },
  "sidebar-link-attributes": {
    ar: "خصائص المنتجات: تعريف خصائص قابلة للفلترة (مقاس، لون، مادة) لاستخدامها في صفحات المنتج وفي الفلاتر الجانبية.",
    en: "Product attributes: define filterable attributes (size, color, material) used on product pages and in sidebar filters.",
  },
  "sidebar-link-sidebar-filters": {
    ar: "إعدادات الفلاتر الجانبية: تحديد أيّ خصائص تظهر كفلاتر في صفحات الأقسام لمساعدة العميل على تضييق نتائج البحث.",
    en: "Sidebar filters config: choose which attributes appear as filters on category pages to help customers narrow results.",
  },
  "sidebar-link-size-guides": {
    ar: "أدلة المقاسات: جداول مقاسات قابلة للربط بكل منتج لمساعدة العميل على اختيار المقاس الصحيح.",
    en: "Size guides: size charts you can attach to products to help customers pick the right size.",
  },
  "sidebar-link-brands": {
    ar: "الماركات: إدارة الماركات/الصانعين، شعاراتهم، وصفحاتهم في المتجر.",
    en: "Brands: manage brand/manufacturer pages, logos, and assignments to products.",
  },
  "sidebar-link-categories": {
    ar: "الأقسام: إدارة شجرة أقسام المتجر (الأقسام والأقسام الفرعية) وصورها وترتيبها.",
    en: "Categories: manage the store category tree (parents and children), images, and ordering.",
  },
  "sidebar-link-product-options": {
    ar: "خيارات المنتجات: تعريف خيارات قابلة للاختيار من العميل (نقش، نص محفور، عبوة هدية) مع تكلفة إضافية أو بدونها.",
    en: "Product options: define customer-selectable options (engraving, gift wrap, custom text) with optional surcharges.",
  },
  "sidebar-link-extensions": {
    ar: "الإضافات: تثبيت وإدارة إضافات الطرف الثالث (دفع، شحن، تكامل، تقارير).",
    en: "Extensions: install and manage third-party extensions (payment, shipping, integrations, reports).",
  },
  "sidebar-link-layouts": {
    ar: "التخطيطات: تحديد كيفية تركيب صفحات المتجر (الصفحة الرئيسية، صفحة المنتج، صفحة القسم) من Modules قابلة للسحب والإفلات.",
    en: "Layouts: compose storefront pages (home, product, category) by drag-and-dropping modules.",
  },
  "sidebar-link-modules": {
    ar: "الموديولات: إدارة كتل المحتوى القابلة للإدراج في التخطيطات (لافتات، عرض منتجات، نص مخصص...).",
    en: "Modules: manage the content blocks insertable into layouts (banners, product showcases, custom text…).",
  },
  "sidebar-link-product-extras": {
    ar: "إضافات المنتج: حقول وميزات إضافية تُضاف على صفحة المنتج (تنزيلات، فيديوهات، شروحات).",
    en: "Product extras: additional fields/features on the product page (downloads, videos, tutorials).",
  },
  "sidebar-link-file-manager": {
    ar: "مدير الملفات: تصفّح ورفع وحذف الصور والملفات المخزّنة في متجرك.",
    en: "File manager: browse, upload, and delete images/files stored in your store.",
  },
  "sidebar-link-payment-methods-v2": {
    ar: "طرق الدفع الجديدة: تفعيل وضبط بوابات الدفع المتاحة (HyperPay، Stripe، تحويل بنكي، الدفع عند الاستلام...).",
    en: "Payment methods (new): enable and configure available payment gateways (HyperPay, Stripe, bank transfer, COD…).",
  },
  "sidebar-link-shipping-providers-v2": {
    ar: "شركات الشحن الجديدة: ربط شركات الشحن مع المتجر، ضبط الأسعار والمناطق المغطّاة.",
    en: "Shipping providers (new): connect carriers, configure rates and supported zones.",
  },
  "sidebar-link-marketplace": {
    ar: "متجر الإضافات: تصفّح إضافات وقوالب جاهزة من Nexus Cart يمكن تثبيتها بنقرة.",
    en: "Marketplace: browse Nexus Cart-curated extensions and themes installable in one click.",
  },
  "sidebar-link-accounting": {
    ar: "النظام المحاسبي: دفتر اليومية، الحسابات، القيود، وتقارير الأرباح والمصروفات.",
    en: "Accounting hub: journal, chart of accounts, entries, and profit/expense reports.",
  },
  "sidebar-link-purchase-orders": {
    ar: "أوامر الشراء: إصدار طلبات شراء للموردين، استلام البضاعة، وتحديث المخزون والتكاليف.",
    en: "Purchase orders: issue PO to suppliers, receive goods, update stock and costs.",
  },
  "sidebar-link-suppliers": {
    ar: "الموردين: إدارة بيانات الموردين والاتفاقيات معهم وتاريخ الشراء.",
    en: "Suppliers: manage supplier records, terms, and purchase history.",
  },
  "sidebar-link-mto-orders": {
    ar: "عروض الإنتاج بالطلب (MTO): جولات تسعير من الموردين على طلبيات بمواصفات محدّدة.",
    en: "Make-To-Order quote rounds: collect pricing from suppliers for spec-driven custom orders.",
  },
  "sidebar-link-expense-categories": {
    ar: "تصنيفات المصروفات: إنشاء أنواع المصروفات (إيجار، مرتبات، تسويق...) لتنظيم المحاسبة.",
    en: "Expense categories: define expense types (rent, salaries, marketing…) to organize bookkeeping.",
  },
  "sidebar-link-order-totals": {
    ar: "إجماليات الطلب: ضبط أسطر الإجماليات الظاهرة في الفاتورة (المجموع الفرعي، الشحن، الضريبة، الكوبون، النهائي) وترتيبها.",
    en: "Order totals: configure the totals lines shown on the invoice (subtotal, shipping, tax, coupon, grand total) and their order.",
  },
  "sidebar-link-inventory": {
    ar: "المخزون: عرض كميات كل منتج عبر المستودعات، التسويات، تنبيهات نقص المخزون.",
    en: "Inventory: per-product stock across warehouses, adjustments, and low-stock alerts.",
  },
  "sidebar-link-packaging": {
    ar: "التغليف والتعبئة: إدارة مواد التغليف وتكلفتها وربطها بأنواع المنتجات.",
    en: "Packaging: manage packaging materials, costs, and links to product types.",
  },
  "sidebar-link-warehouses": {
    ar: "المستودعات: تعريف مواقع التخزين، الكميات في كل مستودع، وقواعد الشحن من المستودع الأقرب.",
    en: "Warehouses: define storage locations, per-warehouse stock, and ship-from-nearest rules.",
  },
  "sidebar-link-companies": {
    ar: "شركات B2B: إدارة الحسابات المؤسسية، حدود الائتمان، أسعار خاصة بكل شركة.",
    en: "B2B companies: manage enterprise accounts, credit limits, and per-company pricing.",
  },
  "sidebar-link-quotes": {
    ar: "عروض الأسعار: إنشاء وإدارة عروض أسعار للعملاء قبل تحويلها إلى طلبات.",
    en: "Quotes: create and manage quotations before converting them into orders.",
  },
  "sidebar-link-pricing-rules": {
    ar: "قواعد التسعير: قواعد تخفيض/ترفيع آلي للأسعار حسب المجموعة، الكمية، الفترة، أو القسم.",
    en: "Pricing rules: automatic price adjustments by customer group, quantity, period, or category.",
  },
  "sidebar-link-tax-system": {
    ar: "نظام الضرائب: تعريف معدّلات الضريبة (VAT)، فئاتها، وقواعد تطبيقها حسب المنطقة أو نوع المنتج.",
    en: "Tax system: define VAT rates, classes, and rules per region or product type.",
  },
  "sidebar-link-tax-compliance": {
    ar: "الامتثال الضريبي: لوحة متابعة التزام المتجر بمتطلبات ضريبة القيمة المضافة وZATCA.",
    en: "Tax compliance: dashboard tracking VAT/ZATCA compliance status.",
  },
  "sidebar-link-price-history": {
    ar: "تاريخ الأسعار: سجل تغييرات السعر لكل منتج عبر الزمن (مفيد للتحليل وإلتزامات إعلان السعر السابق).",
    en: "Price history: per-product price-change log over time (useful for analysis and prior-price disclosure rules).",
  },
  "sidebar-link-campaign-roi": {
    ar: "تقارير ROI الحملات: قياس عائد الاستثمار لكل حملة تسويقية (إنفاق مقابل إيراد محقَّق).",
    en: "Campaign ROI: measure return on investment for each marketing campaign (spend vs revenue).",
  },
  "sidebar-link-wishlist-analytics": {
    ar: "تحليلات قوائم الأمنيات: المنتجات الأكثر إضافةً للأمنيات وتلك التي تتحول لطلبات فعلية.",
    en: "Wishlist analytics: most-wishlisted products and which actually convert to orders.",
  },
  "sidebar-link-conversion-tracking": {
    ar: "تتبّع التحويلات: نقاط التحويل (إضافة للسلة، بدء الدفع، إكمال الطلب) ومعدّل تحويل كل خطوة.",
    en: "Conversion tracking: funnel steps (add-to-cart, begin checkout, purchase) with per-step conversion rates.",
  },
  "sidebar-link-capi-logs": {
    ar: "سجلات CAPI: تتبّع الأحداث المرسلة إلى Conversions API (Meta) للتأكّد من نجاح إرسالها وتشخيص الأخطاء.",
    en: "CAPI logs: track events sent to Meta Conversions API to verify delivery and debug failures.",
  },
  "sidebar-link-blog-settings": {
    ar: "إعدادات المدونة: ضبط عنوان المدونة، الوصف، عدد المقالات في الصفحة، التعليقات، والتقييمات.",
    en: "Blog settings: configure title, description, posts per page, comments, and ratings.",
  },
  "sidebar-link-blog-categories": {
    ar: "أقسام المدونة: تنظيم المقالات في أقسام لتسهيل تصفّح القارئ.",
    en: "Blog categories: organize posts into sections for easier reader navigation.",
  },
  "sidebar-link-blog-posts": {
    ar: "المقالات: إنشاء وتحرير ونشر المقالات وتعيين أقسام ووسوم وصور.",
    en: "Posts: create, edit, publish posts; assign categories, tags, and images.",
  },
  "sidebar-link-blog-comments": {
    ar: "التعليقات: مراجعة تعليقات القرّاء قبل ظهورها، حذف غير المرغوب، الردّ من لوحة التحكم.",
    en: "Comments: moderate reader comments before they appear, delete spam, and reply from the admin.",
  },
  "sidebar-link-ai-studio": {
    ar: "استوديو الذكاء الاصطناعي: توليد مقالات/أوصاف منتجات/منشورات تسويقية بالذكاء الاصطناعي بكلفة أقل.",
    en: "AI Studio: generate articles/product descriptions/marketing posts with AI to save time.",
  },
  "sidebar-link-settings": {
    ar: "إعدادات المتجر العامة: اسم المتجر، الشعار، اللغة الافتراضية، العملة، معلومات الاتصال، الدفع، الشحن، البريد، الإشعارات...",
    en: "General store settings: store name, logo, default language, currency, contact info, payments, shipping, email, notifications…",
  },
  "sidebar-link-notification-settings": {
    ar: "إعدادات الإشعارات: ضبط أيّ أحداث تُرسل إشعاراً، عبر أيّ قناة (داخلي/إيميل/واتساب)، ولأيّ مسؤولين.",
    en: "Notification settings: configure which events trigger a notification, via which channel (in-app/email/WhatsApp), and to whom.",
  },
  "sidebar-link-custom-domain": {
    ar: "الدومين المخصص: ربط دومين خاص بك (مثلاً luxurygive.com) بمتجرك بدلاً من رابط Nexus الفرعي.",
    en: "Custom domain: connect your own domain (e.g. luxurygive.com) instead of the Nexus subdomain.",
  },
  "sidebar-link-admin-groups": {
    ar: "مجموعات المستخدمين (الصلاحيات): إنشاء أدوار (محاسب، مدير محتوى، شحن) وتحديد ما يمكن لكل دور رؤيته أو تعديله.",
    en: "User groups (ACL): create roles (accountant, content manager, shipping) and define what each role can see or edit.",
  },
  "sidebar-link-languages": {
    ar: "اللغات: تفعيل لغات المتجر والترجمة وضبط اللغة الافتراضية.",
    en: "Languages: enable store languages and translation, and set the default language.",
  },
  "sidebar-link-return-config": {
    ar: "إعدادات المرتجعات: مدّة قبول الإرجاع، الأسباب المتاحة، طرق ردّ المبلغ.",
    en: "Returns config: return window, available reasons, refund methods.",
  },
  "sidebar-link-order-settings": {
    ar: "إعدادات الطلبات: التسلسل الرقمي للطلبات، الحالات الافتراضية، حجوزات المخزون، السماح بطلبات الضيف.",
    en: "Order settings: order numbering, default statuses, stock holds, guest checkout permission.",
  },
  "sidebar-link-weight-length-classes": {
    ar: "وحدات الوزن والطول: تعريف الوحدات (كجم، جم، سم، م) ومعدّلات تحويلها.",
    en: "Weight/length classes: define units (kg, g, cm, m) and conversion ratios.",
  },
  "sidebar-link-countries": {
    ar: "الدول والمناطق: قائمة الدول والمناطق المغطّاة بالشحن أو الفوترة.",
    en: "Countries & zones: list of countries/regions supported for shipping or billing.",
  },
  "sidebar-link-order-statuses": {
    ar: "حالات الطلبات: تخصيص أسماء الحالات وألوانها (قيد المعالجة، تم الشحن، تم التسليم، ...) وقواعد الانتقال بينها.",
    en: "Order statuses: customize status names and colors (processing, shipped, delivered…) and transition rules.",
  },
  "sidebar-link-shipping-zones": {
    ar: "مناطق الشحن: تعريف مناطق جغرافية وأسعار شحن مختلفة لكل منطقة.",
    en: "Shipping zones: define geographic zones with different shipping rates per zone.",
  },
  "sidebar-link-stores": {
    ar: "إدارة المتاجر: تشغيل عدّة متاجر تحت نفس الحساب (متعدد المتاجر) وتبديل بينها.",
    en: "Multi-store: run multiple stores under one account and switch between them.",
  },
  "sidebar-link-linked-stores": {
    ar: "ربط المتاجر: مزامنة منتجات/طلبات بين متجرك ومنصّات خارجية (Salla، Zid، Shopify ...).",
    en: "Linked stores: sync products/orders between your store and external platforms (Salla, Zid, Shopify…).",
  },
  "sidebar-link-event-hooks": {
    ar: "الأحداث وWebhooks: تسجيل URL خارجية تُستدعى تلقائياً عند حدث (طلب جديد، تحديث منتج...).",
    en: "Events & webhooks: register external URLs called automatically on events (new order, product update…).",
  },
  "sidebar-link-trash": {
    ar: "سلة المحذوفات: استعادة العناصر المحذوفة (منتجات، طلبات، عملاء) خلال فترة الاحتفاظ.",
    en: "Trash: restore deleted items (products, orders, customers) within the retention window.",
  },
  "sidebar-link-indexer": {
    ar: "إدارة الفهارس: إعادة بناء فهارس البحث والأسعار يدوياً عند ملاحظة بطء في البحث.",
    en: "Index management: manually rebuild search/price indexes when search feels slow.",
  },
  "sidebar-link-security-2fa": {
    ar: "المصادقة الثنائية (2FA): تفعيل خطوة تحقّق إضافية على حسابات لوحة التحكم لحماية المتجر.",
    en: "Two-Factor Auth (2FA): require an extra verification step on admin logins to secure the store.",
  },
  "sidebar-link-apps": {
    ar: "التطبيقات المُثبَّتة: إدارة التطبيقات الخارجية المرتبطة بحسابك ومنحها/سحب صلاحياتها.",
    en: "Installed apps: manage external apps connected to your account and grant/revoke their permissions.",
  },
  "sidebar-link-personal-tokens": {
    ar: "مفاتيح API الخاصّة: إنشاء/إلغاء توكنات شخصية لاستدعاء API المتجر من سكربتاتك.",
    en: "Personal API tokens: create/revoke personal tokens to call your store's API from scripts.",
  },
  "sidebar-link-import-export": {
    ar: "الاستيراد والتصدير: رفع ملف Excel/CSV لإضافة منتجات/عملاء/طلبات بالجملة، أو تصدير البيانات.",
    en: "Import/export: upload Excel/CSV to bulk-add products/customers/orders, or export data.",
  },
  "sidebar-link-url-rewrites": {
    ar: "إعادة كتابة الروابط: تعديل شكل روابط المنتجات/الأقسام لتكون أصدق للـSEO ولترحيل من منصّات أخرى.",
    en: "URL rewrites: customize product/category URL shapes for better SEO and migrations.",
  },
  "sidebar-link-cache-management": {
    ar: "إدارة الكاش: مسح الكاش المخزَّن (HTML/صور/استعلامات) عند ملاحظة بقاء بيانات قديمة.",
    en: "Cache management: clear stored caches (HTML/images/queries) when stale data is showing.",
  },
  "sidebar-link-system-performance__REMOVED": {
    ar: "أداء النظام: مؤشرات سرعة المتجر (Web Vitals)، استهلاك السيرفر، أبطأ الصفحات.",
    en: "System performance: store speed metrics (Web Vitals), server load, slowest pages.",
  },
  "sidebar-link-system-monitoring": {
    ar: "مراقبة الأخطاء: عرض أحدث أخطاء المتجر (front-end + back-end) للتشخيص السريع.",
    en: "Error monitoring: latest store errors (front-end + back-end) for quick diagnosis.",
  },
  "sidebar-link-scheduled-jobs__REMOVED": {
    ar: "جدولة المهام: متابعة المهام التلقائية (مزامنات، نسخ احتياطي، إرسال إيميلات) وموعد تشغيلها التالي.",
    en: "Scheduled jobs: monitor automatic jobs (syncs, backups, email sends) and their next run.",
  },
  "sidebar-link-anti-fraud": {
    ar: "مكافحة الاحتيال: قواعد كشف الطلبات المشبوهة (نفس IP متكرّر، بطاقات مرفوضة، عناوين متضاربة).",
    en: "Anti-fraud: detection rules for suspicious orders (repeated IP, declined cards, mismatched addresses).",
  },
  "sidebar-link-data-privacy": {
    ar: "البيانات والخصوصية (PDPL/GDPR): مكان واحد لإدارة طلبات الخصوصية، سجل الموافقات، تصدير بيانات المتجر، وإغلاقه.",
    en: "Data & Privacy (PDPL/GDPR): one place for privacy requests, consent logs, full store export, and store closure.",
  },
  "sidebar-link-db-backup": {
    ar: "النسخ الاحتياطي: إنشاء وتنزيل نسخ من قاعدة بيانات متجرك لاستعادتها وقت الحاجة.",
    en: "DB backup: create and download backups of your store database for emergency restore.",
  },
  "sidebar-link-zatca-onboarding": {
    ar: "ربط ZATCA (المرحلة الثانية): تسجيل المتجر مع هيئة الزكاة والضريبة لإصدار فواتير إلكترونية معتمدة.",
    en: "ZATCA onboarding (Phase 2): register the store with ZATCA to issue compliant e-invoices.",
  },
  "sidebar-link-custom-css": {
    ar: "محرر CSS مخصص: كتابة CSS إضافي لتعديل مظهر المتجر دون تغيير الكود الأصلي. يُفحص المحتوى أمنياً قبل الحفظ.",
    en: "Custom CSS editor: write extra CSS to tweak the storefront look without touching source code. Content is security-scanned before saving.",
  },

  // ─── SEO page tabs ────────────────────────────────────────────────────
  "tab-seo-dashboard": {
    ar: "تبويب «لوحة التحكم»: مؤشّر صحة الـSEO الكلّي، عدد الصفحات بدون عنوان/وصف، وأبرز المشاكل التي يجب معالجتها.",
    en: "“Dashboard” tab: overall SEO health score, count of pages missing title/description, and top issues to fix.",
  },
  "tab-seo-urls": {
    ar: "تبويب «روابط SEO»: قائمة كل صفحات المتجر مع عناوين الميتا، أوصافها، وروابطها — قابلة للتعديل المباشر.",
    en: "“SEO URLs” tab: every store page with its meta title, description, and URL — directly editable.",
  },
  "tab-seo-mass": {
    ar: "تبويب «التحديث الجماعي»: تطبيق قوالب عناوين/أوصاف على كل المنتجات أو الأقسام دفعة واحدة (مثلاً «{اسم المنتج} — {الماركة}»).",
    en: "“Mass update” tab: apply title/description templates to all products or categories at once (e.g. “{product} — {brand}”).",
  },
  "tab-seo-redirects": {
    ar: "تبويب «إعادة التوجيه»: إعداد قواعد 301/302 لتحويل روابط قديمة إلى جديدة، مفيد بعد تغيير هيكل الموقع.",
    en: "“Redirects” tab: configure 301/302 rules to send old URLs to new ones — useful after URL structure changes.",
  },
  "tab-seo-rich": {
    ar: "تبويب «المقتطفات المنسّقة»: إدخال بيانات المنظَّمة (Organization، LocalBusiness) التي تظهر كنتائج غنيّة في Google.",
    en: "“Rich snippets” tab: enter structured data (Organization, LocalBusiness) that appears as Google rich results.",
  },
  "tab-seo-analytics": {
    ar: "تبويب «التحليلات»: ربط Google Analytics وGoogle Tag Manager وSearch Console وبكسلات Meta/Snap/TikTok بالمتجر.",
    en: "“Analytics” tab: connect Google Analytics, Tag Manager, Search Console, and Meta/Snap/TikTok pixels to the store.",
  },
  "tab-seo-sitemap": {
    ar: "تبويب «خريطة الموقع»: عرض/تحديث ملف sitemap.xml الذي تستخدمه محركات البحث لاكتشاف صفحات المتجر.",
    en: "“Sitemap” tab: view/refresh sitemap.xml used by search engines to discover your store pages.",
  },
  "tab-seo-robots": {
    ar: "تبويب «ملف Robots.txt»: تحرير ملف robots.txt الذي يخبر محركات البحث أيّ صفحات يُسمح بفهرستها وأيّها لا. كن حذراً — خطأ هنا قد يحجب متجرك عن Google كلياً.",
    en: "“Robots.txt” tab: edit robots.txt — tells search engines which pages to crawl. Be careful: a mistake here can hide your whole store from Google.",
  },
  "tab-seo-editor": {
    ar: "تبويب «المحرر الجدولي»: تعديل عناوين وأوصاف الميتا لمنتجات/أقسام/ماركات/صفحات/مقالات في جدول واحد كأنه Excel.",
    en: "“Table editor” tab: edit meta titles/descriptions for products/categories/brands/pages/posts in a spreadsheet-style grid.",
  },
};

/**
 * Pattern fallback. First match wins. Each pattern receives the testId,
 * the derived label, and the route, and returns a help entry or null.
 */
const PATTERNS: Array<{
  test: (ctx: { testId: string; label: string; route: string }) => boolean;
  help: ButtonHelp;
}> = [
  // Generic save / submit
  {
    test: ({ testId, label }) =>
      /(^|[-_])save([-_]|$)|^btn-save|^submit/i.test(testId) ||
      /^(حفظ|حفظ التغييرات|save|submit)$/i.test(label),
    help: {
      ar: "يحفظ كل التغييرات التي قمت بها على هذه الصفحة في قاعدة البيانات.",
      en: "Saves every change you made on this page to the database.",
    },
  },
  // Cancel
  {
    test: ({ testId, label }) =>
      /(^|[-_])cancel([-_]|$)|^btn-cancel/i.test(testId) || /^(إلغاء|cancel)$/i.test(label),
    help: {
      ar: "يلغي العملية الحالية ويُغلق هذا الحوار/النموذج دون حفظ أي تغييرات.",
      en: "Cancels the current operation and closes this dialog/form without saving any changes.",
    },
  },
  // Confirm
  {
    test: ({ testId, label }) =>
      /(^|[-_])confirm([-_]|$)|^btn-confirm/i.test(testId) || /^(تأكيد|confirm|ok)$/i.test(label),
    help: {
      ar: "يؤكِّد العملية ويُنفّذها. تأكّد من قراءة التفاصيل قبل الضغط — قد يكون الإجراء غير قابل للتراجع.",
      en: "Confirms and executes the operation. Read the details first — some actions cannot be undone.",
    },
  },
  // Delete (anything)
  {
    test: ({ testId, label }) =>
      /(^|[-_])(delete|remove|trash)([-_]|$)/i.test(testId) ||
      /^(حذف|إزالة|delete|remove)$/i.test(label),
    help: {
      ar: "يحذف العنصر نهائياً. لا يمكن التراجع. يُفضّل تأكيد الحذف قبل التنفيذ.",
      en: "Permanently deletes the item. Cannot be undone. Confirm before proceeding.",
    },
  },
  // Add / new / create
  {
    test: ({ testId, label }) =>
      /(^|[-_])(add|new|create)([-_]|$)|^btn-(add|new|create)/i.test(testId) ||
      /^(إضافة|جديد|إنشاء|add|new|create)/i.test(label),
    help: {
      ar: "يفتح نموذجاً لإنشاء عنصر جديد. املأ الحقول المطلوبة ثم احفظ.",
      en: "Opens a form to create a new item. Fill in the required fields and save.",
    },
  },
  // Edit
  {
    test: ({ testId, label }) =>
      /(^|[-_])edit([-_]|$)/i.test(testId) || /^(تعديل|تحرير|edit)$/i.test(label),
    help: {
      ar: "يفتح هذا العنصر في وضع التعديل لتغيير بياناته.",
      en: "Opens this item in edit mode so you can change its data.",
    },
  },
  // Refresh / reload
  {
    test: ({ testId, label }) =>
      /(^|[-_])(refresh|reload)([-_]|$)/i.test(testId) ||
      /^(تحديث|إعادة تحميل|refresh|reload)$/i.test(label),
    help: {
      ar: "يُحدِّث البيانات المعروضة ويجلب أحدث الإصدار من السيرفر.",
      en: "Refreshes the displayed data and pulls the latest version from the server.",
    },
  },
  // Search input
  {
    test: ({ testId, label }) =>
      /(^|[-_])search([-_]|$)|^input-search/i.test(testId) || /^(بحث|search)/i.test(label),
    help: {
      ar: "حقل بحث: اكتب كلمة وستُصفَّى القائمة لحظياً لتُظهر النتائج المطابقة فقط.",
      en: "Search field: type a keyword and the list will filter live to show matching results.",
    },
  },
  // Bulk actions
  {
    test: ({ testId }) => /(^|[-_])bulk([-_]|$)/i.test(testId),
    help: {
      ar: "إجراء جماعي: يُطبَّق على كل العناصر التي حدّدتها مسبقاً (المحدّدة بمربعات الاختيار).",
      en: "Bulk action: applies to every item you previously selected (via checkboxes).",
    },
  },
  // Export
  {
    test: ({ testId, label }) =>
      /(^|[-_])(export|download)([-_]|$)/i.test(testId) ||
      /^(تصدير|تحميل|export|download)/i.test(label),
    help: {
      ar: "يُصدِّر البيانات الحالية إلى ملف يمكنك تحميله (Excel/CSV/PDF حسب السياق).",
      en: "Exports the current data to a downloadable file (Excel/CSV/PDF depending on context).",
    },
  },
  // Import
  {
    test: ({ testId, label }) =>
      /(^|[-_])(import|upload)([-_]|$)/i.test(testId) ||
      /^(استيراد|رفع|import|upload)/i.test(label),
    help: {
      ar: "يستورد بيانات من ملف ترفعه من جهازك. تأكَّد أن صيغة الملف مطابقة للقالب المتوقَّع.",
      en: "Imports data from a file you upload from your device. Make sure the file format matches the expected template.",
    },
  },
  // Settings / config
  {
    test: ({ testId, label }) =>
      /(^|[-_])(settings|config|configure)([-_]|$)/i.test(testId) ||
      /^(إعدادات|تكوين|settings)/i.test(label),
    help: {
      ar: "يفتح صفحة الإعدادات لتخصيص خيارات هذا القسم.",
      en: "Opens the settings page to customize this section's options.",
    },
  },
  // Stat / KPI cards
  {
    test: ({ testId }) => /^(stat-|kpi-)/i.test(testId),
    help: {
      ar: "كرت إحصائي يعرض رقماً مهماً عن متجرك. قد يكون قابلاً للضغط لعرض التفاصيل أو تطبيق فلتر.",
      en: "A stat card showing an important number about your store. May be clickable to drill in or apply a filter.",
    },
  },
  // Tabs (generic)
  {
    test: ({ testId }) => /^tab-/i.test(testId),
    help: {
      ar: "تبويب يبدّل العرض إلى قسم آخر من نفس الصفحة، دون مغادرتها.",
      en: "A tab that switches the view to another section of the same page without leaving it.",
    },
  },
  // Selects (generic)
  {
    test: ({ testId }) => /^select-/i.test(testId),
    help: {
      ar: "قائمة اختيار: اختر قيمة لتصفية البيانات أو لتغيير الإعداد المتعلق بهذا الحقل.",
      en: "A select dropdown: pick a value to filter the data or change the setting tied to this field.",
    },
  },
  // Inputs (generic)
  {
    test: ({ testId }) => /^(input|textarea|field)-/i.test(testId),
    help: {
      ar: "حقل إدخال: اكتب القيمة المطلوبة هنا. ستُحفظ عند الضغط على زر الحفظ في الأسفل.",
      en: "An input field: type the required value here. Changes are saved when you press the save button at the bottom.",
    },
  },
  // Open
  {
    test: ({ testId, label }) =>
      /(^|[-_])open([-_]|$)/i.test(testId) || /^(فتح|open)$/i.test(label),
    help: {
      ar: "يفتح هذا العنصر لعرض كامل تفاصيله.",
      en: "Opens this item to show its full details.",
    },
  },
  // Archive
  {
    test: ({ testId, label }) =>
      /(^|[-_])archive([-_]|$)/i.test(testId) || /^(أرشفة|أرشيف|archive)$/i.test(label),
    help: {
      ar: "يُؤرشف العنصر: يخفيه من القائمة الرئيسية لكن لا يحذفه. يمكنك استعادته من الأرشيف لاحقاً.",
      en: "Archives the item: hides it from the main list but does not delete it. You can restore it later from the archive.",
    },
  },
  // Notification bell (header)
  {
    test: ({ testId, label }) =>
      /notif|bell/i.test(testId) || /(الإشعارات|notifications)/i.test(label),
    help: {
      ar: "يفتح مركز الإشعارات: كل أحداث المتجر (طلبات، عملاء، تنبيهات) في مكان واحد.",
      en: "Opens the notification center: all your store events (orders, customers, alerts) in one place.",
    },
  },
  // Logout
  {
    test: ({ testId, label }) =>
      /logout|sign-out|signout/i.test(testId) || /(تسجيل الخروج|logout|sign out)/i.test(label),
    help: {
      ar: "يُسجِّل خروجك من لوحة التحكم. ستحتاج إلى تسجيل الدخول مجدّداً لاحقاً.",
      en: "Signs you out of the admin panel. You'll need to log in again later.",
    },
  },
  // Back to store
  {
    test: ({ label }) => /(العودة للمتجر|back to (store|shop))/i.test(label),
    help: {
      ar: "يأخذك من لوحة التحكم إلى الواجهة الأمامية لمتجرك (ما يراه العميل).",
      en: "Takes you from the admin panel to your storefront (what customers see).",
    },
  },
  // Sidebar nav links — STRICT: only fires for explicit nav/menu/link/sidebar
  // testids. Previously matched any short label and produced misleading
  // explanations for ordinary buttons (e.g. tabs without a testid).
  {
    test: ({ testId }) =>
      /^(nav-|menu-|link-|sidebar-|side-nav-)/i.test(testId),
    help: {
      ar: "رابط في قائمة لوحة التحكم: اضغطه للانتقال إلى هذا القسم وإدارته.",
      en: "A link in the admin sidebar: tap to navigate to that section and manage it.",
    },
  },
];

export interface ButtonHelpResult extends ButtonHelp {
  source: "override" | "exact" | "prefix" | "pattern" | "fallback";
}

import { getOverride } from "./help-overrides-store";

export function lookupButtonHelp(
  testId: string | null | undefined,
  label: string,
  route: string,
): ButtonHelpResult {
  const id = (testId || "").trim();
  const lab = (label || "").trim();

  // 0. Platform-level override (edited by super-admins) — wins over everything.
  if (id) {
    const ov = getOverride(id);
    if (ov) return { ar: ov.ar, en: ov.en, source: "override" };
  }

  // 1. Exact testId match
  if (id && EXACT[id]) {
    return { ...EXACT[id], source: "exact" };
  }

  // 2. Prefix match (entries ending with `-*`)
  if (id) {
    for (const key of Object.keys(EXACT)) {
      if (key.endsWith("-*") && id.startsWith(key.slice(0, -1))) {
        return { ...EXACT[key], source: "prefix" };
      }
    }
  }

  // 3. Pattern match
  for (const p of PATTERNS) {
    if (p.test({ testId: id, label: lab, route })) {
      return { ...p.help, source: "pattern" };
    }
  }

  // 4. Fallback — at least give a useful generic message instead of just echoing.
  return {
    ar: `زر باسم "${lab || "(بدون عنوان)"}". الشرح التفصيلي لهذا الزر لم يُكتب بعد — اضغط (تنفيذ الآن) لمعرفة ما يفعل، أو راسلنا لإضافة شرحه.`,
    en: `Button labeled "${lab || "(no label)"}". A detailed help entry hasn't been written for it yet — tap (Run now) to see what it does, or contact us to add an explanation.`,
    source: "fallback",
  };
}
